import { ReportFinding } from './types';
import { RunReading, firstValue, formatNumber, toNumber } from './runs';
import { BlockDefinition, BlockInput, defineBlock } from './blocks';
import { ReadingSelector, describeSelector, isEmptySelector, readingField, selectReadings } from './bindings';
import { Dimension, classify, dimensionRef } from './dimensions';
import { FigureBucket, ReadingBucket, StepBlock, StepBucket, figuresIn, readingsIn, stepsIn } from './placement';
import { runChecks, shownByChecks } from './checks';
import { isEmptyProcedureParameters, procedureParameters } from './procedureParameters';
import { LIMIT_BLOCKS } from './limits';
import { defineValues, v } from './schema';
import { finding } from './verdict';

/**
 * The SDK's block types: what the general procedure test report is made of,
 * and what every composed report can lay out. Each is the
 * pure half; src/frontend/reports/blocks draws them.
 */

const READING_BUCKETS = ['introduction', 'dut', 'parameters'] as const;
const FIGURE_BUCKETS = ['introduction', 'dut', 'diagram', 'setup', 'appendix'] as const;
const STEP_BUCKETS = ['data', 'validation', 'analysis', 'conclusion', 'appendix'] as const;

const BUCKET_LABELS: Record<string, string> = {
  introduction: 'Introduction',
  dut: 'DUT information',
  parameters: 'Parameters (inputs and setup values)',
  diagram: 'Test diagram',
  setup: 'Test setup photos',
  data: 'Test data (stimulus and acquisition)',
  validation: 'Data validation',
  analysis: 'Analysis',
  conclusion: 'Verification',
  appendix: 'Appendix',
};

/** The value of a per-run field a block asks for. */
export function fieldValue(input: Pick<BlockInput, 'values' | 'block'>): string {
  const fields = input.values.fields;
  const value = fields && typeof fields === 'object' ? (fields as Record<string, unknown>)[input.block.id] : undefined;
  return typeof value === 'string' ? value : '';
}

// ── text and fields ─────────────────────────────────────────────────────────

export const textBlock = defineBlock({
  type: 'text',
  title: 'Text',
  description: 'A paragraph set once in the layout, printed in every report made from it.',
  group: 'Text',
  props: defineValues({
    text: v.text({ label: 'Text', multiline: true }),
    fallback: v.choice(['procedure-description'] as const, { label: 'When the text is empty, print', labels: { 'procedure-description': 'the procedure’s description' } }),
  }),
  isEmpty: ({ props, run }) => !props.text.trim() && !(props.fallback === 'procedure-description' && run.description?.trim()),
});

export const fieldBlock = defineBlock({
  type: 'field',
  title: 'Field to fill in',
  description: 'A box the report’s owner fills in on each run, such as a conclusion or notes.',
  group: 'Text',
  props: defineValues({
    label: v.text({ label: 'Label', default: 'Notes' }),
    help: v.text({ label: 'Hint for whoever fills it in', optional: true }),
    multiline: v.boolean({ label: 'Several lines', default: true }),
    required: v.boolean({ label: 'Must be filled in before the report passes', default: false }),
  }),
  fields: ({ block, props }) => [{ key: block.id, label: props.label || 'Notes', optional: !props.required, multiline: props.multiline }],
  findings: (input) => (input.props.required && !fieldValue(input).trim() ? [finding.gap(`field-${input.block.id}`, `${input.props.label || 'A required field'} is not filled in.`)] : []),
  isEmpty: (input) => !fieldValue(input).trim(),
});

// ── run data ────────────────────────────────────────────────────────────────

export const readingsBlock = defineBlock({
  type: 'readings',
  title: 'Recorded values',
  description: 'A table of the values the run recorded in one part of the procedure.',
  group: 'Run data',
  props: defineValues({
    bucket: v.choice(READING_BUCKETS, { label: 'From', default: 'parameters', labels: BUCKET_LABELS }),
    title: v.text({ label: 'Table title', optional: true }),
    showStep: v.boolean({ label: 'Show the step each value was recorded at', default: true }),
  }),
  isEmpty: ({ placed, props }) => readingsIn(placed, (props.bucket || 'parameters') as ReadingBucket).length === 0,
});

export const galleryBlock = defineBlock({
  type: 'gallery',
  title: 'Pictures',
  description: 'The photos and diagrams the run recorded in one part of the procedure, numbered as figures.',
  group: 'Run data',
  props: defineValues({
    bucket: v.choice(FIGURE_BUCKETS, { label: 'From', default: 'setup', labels: BUCKET_LABELS }),
    title: v.text({ label: 'Title', optional: true }),
  }),
  isEmpty: ({ placed, props }) => figuresIn(placed, (props.bucket || 'setup') as FigureBucket).length === 0,
});

export const dutBlock = defineBlock({
  type: 'dut',
  title: 'Device under test',
  description: 'The DUT on the run, its identifying parameters and its photos.',
  group: 'Run data',
  props: defineValues({}),
  isEmpty: ({ run, placed }) => !run.dut && placed.dut.readings.length === 0 && placed.dut.figures.length === 0,
});

export const equipmentBlock = defineBlock({
  type: 'equipment',
  title: 'Equipment',
  description: 'The instruments listed on the run.',
  group: 'Run data',
  props: defineValues({}),
  isEmpty: ({ run }) => (run.equipment ?? []).length === 0,
});

const stepsProps = defineValues({
  bucket: v.choice(STEP_BUCKETS, { label: 'From', default: 'data', labels: BUCKET_LABELS }),
  exceptChecked: v.boolean({ label: 'Leave out steps the checks table shows in full', default: false }),
});

/** The steps a steps block prints: its part of the procedure, less the steps a checks table shows in full where it says so. */
export function printedSteps(input: Pick<BlockInput<{ bucket: string; exceptChecked: boolean }>, 'placed' | 'props' | 'run'>): StepBlock[] {
  const blocks = stepsIn(input.placed, (input.props.bucket || 'data') as StepBucket);
  if (!input.props.exceptChecked) return blocks;
  const checks = runChecks(input.run);
  return blocks.filter((b) => !shownByChecks(b, checks));
}

export const stepsBlock = defineBlock({
  type: 'steps',
  title: 'Steps',
  description: 'One subsection per step of one part of the procedure: its values, checks and pictures.',
  group: 'Run data',
  props: stepsProps,
  isEmpty: (input) => printedSteps(input).length === 0,
});

export const dataFilesBlock = defineBlock({
  type: 'data-files',
  title: 'Data files',
  description: 'Every points or data file the run recorded, with where it was recorded.',
  group: 'Record',
  props: defineValues({}),
  isEmpty: ({ placed }) => placed.files.length === 0,
});

// ── results ─────────────────────────────────────────────────────────────────

export interface MatrixCell {
  /** the value of the dimension the matrix is split by; null when it prints one table */
  split: string | null;
  row: string;
  col: string;
  value: number | null;
  limit: number | null;
  state: 'pass' | 'fail' | 'none';
  stepTitle: string;
}

export interface MatrixModel {
  rows: string[];
  cols: string[];
  /** one table per value, in order, where the matrix is split by a third dimension; empty for one table */
  splits: string[];
  cells: MatrixCell[];
  /** why there is no matrix: dimensions not set, nothing measured */
  problem: string | null;
}

const numberOf = (readings: RunReading[]): number | null => {
  for (const r of readings) {
    const n = toNumber(firstValue(r));
    if (n !== null) return n;
  }
  return null;
};

function orderedValues(dimension: Dimension, seen: string[]): string[] {
  const declared = dimension.values.map((d) => d.label);
  return [...declared, ...seen.filter((s) => !declared.includes(s))];
}

/** The values a run reached, in the dimension's order. */
function reachedValues(dimension: Dimension, seen: string[]): string[] {
  return orderedValues(dimension, seen).filter((v) => seen.includes(v));
}

const matrixProps = defineValues({
  title: v.text({ label: 'Title', optional: true }),
  rows: dimensionRef('Rows'),
  cols: dimensionRef('Columns'),
  split: dimensionRef('One table per', { optional: true, help: 'A third dimension: one table for each of its values the run reached, e.g. one per temperature.' }),
  value: readingField({ label: 'Value in each cell' }),
  limit: v.number({ label: 'Upper limit', help: 'The same limit for every cell; leave empty to read it from each step.' }),
  limitReading: readingField({ label: 'Or read the limit from each step', optional: true }),
  unit: v.text({ label: 'Unit', optional: true }),
  bucket: v.choice(STEP_BUCKETS, { label: 'From', default: 'data', labels: BUCKET_LABELS }),
});

type MatrixProps = ReturnType<typeof matrixProps.defaults>;

const noMatrix = (problem: string): MatrixModel => ({ rows: [], cols: [], splits: [], cells: [], problem });

/** The matrix a results block prints: one cell per row × column value (per value of the split, where there is one), from the steps classified there. Pure; the block's findings and its view both read it. */
export function resultsMatrix(input: BlockInput<MatrixProps>): MatrixModel {
  const { props, dimensions, placed, run } = input;
  const rowDim = dimensions.find((d) => d.id === props.rows);
  const colDim = dimensions.find((d) => d.id === props.cols);
  const splitDim = props.split ? dimensions.find((d) => d.id === props.split) : undefined;
  if (!rowDim || !colDim || (props.split && !splitDim)) return noMatrix('Pick the dimensions for the rows and columns in the report’s layout.');
  if (isEmptySelector(props.value as ReadingSelector)) return noMatrix('Pick the reading each cell shows in the report’s layout.');
  const cells: MatrixCell[] = [];
  for (const block of stepsIn(placed, (props.bucket || 'data') as StepBucket)) {
    const readings = selectReadings(block.step, props.value as ReadingSelector);
    if (readings.length === 0) continue;
    const at = classify(block.step, splitDim ? [rowDim, colDim, splitDim] : [rowDim, colDim], run);
    const row = at[rowDim.id];
    const col = at[colDim.id];
    const split = splitDim ? at[splitDim.id] : null;
    if (!row || !col || (splitDim && !split)) continue;
    const value = numberOf(readings);
    const limit = props.limit ?? (isEmptySelector(props.limitReading as ReadingSelector) ? null : numberOf(selectReadings(block.step, props.limitReading as ReadingSelector)));
    const state = value === null || limit === null ? 'none' : value <= limit ? 'pass' : 'fail';
    const existing = cells.findIndex((c) => c.split === split && c.row === row && c.col === col);
    const cell = { split, row, col, value, limit, state, stepTitle: block.title } as MatrixCell;
    // a combination measured twice keeps the worse reading
    if (existing === -1) cells.push(cell);
    else if (value !== null && (cells[existing].value === null || value > (cells[existing].value as number))) cells[existing] = cell;
  }
  const rows = orderedValues(rowDim, Array.from(new Set(cells.map((c) => c.row))));
  const cols = orderedValues(colDim, Array.from(new Set(cells.map((c) => c.col))));
  const splits = splitDim ? reachedValues(splitDim, Array.from(new Set(cells.map((c) => c.split as string)))) : [];
  const where = [rowDim, colDim, ...(splitDim ? [splitDim] : [])].map((d) => d.label.toLowerCase()).join(', ');
  return { rows, cols, splits, cells, problem: cells.length ? null : `No step records ${describeSelector(props.value as ReadingSelector)} at a ${where} the template names.` };
}

export const resultsMatrixBlock: BlockDefinition<MatrixProps> = defineBlock({
  type: 'results-matrix',
  title: 'Results matrix',
  description: 'One reading laid out over two dimensions of the test matrix, each cell judged against a limit — one table per value of a third, where it is split.',
  group: 'Results',
  props: matrixProps,
  findings: (input): ReportFinding[] => {
    const matrix = resultsMatrix(input);
    const unit = input.props.unit ? ` ${input.props.unit}` : '';
    return matrix.cells
      .filter((c) => c.state === 'fail')
      .map((c) => finding.defect(
        `matrix-${input.block.id}-${c.split === null ? '' : `${c.split}-`}${c.row}-${c.col}`,
        `${c.row} at ${c.col}${c.split === null ? '' : ` (${c.split})`}: ${formatNumber(c.value as number)}${unit} exceeds the limit of ${formatNumber(c.limit as number)}${unit}.`,
      ));
  },
  isEmpty: (input) => resultsMatrix(input).cells.length === 0,
});

export interface CoverageModel {
  rows: string[];
  cols: string[];
  /** row → col → the steps that ran there */
  executed: Record<string, Record<string, string[]>>;
  problem: string | null;
}

const coverageProps = defineValues({
  title: v.text({ label: 'Title', optional: true }),
  rows: dimensionRef('Rows'),
  cols: dimensionRef('Columns'),
  bucket: v.choice(STEP_BUCKETS, { label: 'From', default: 'data', labels: BUCKET_LABELS }),
});

type CoverageProps = ReturnType<typeof coverageProps.defaults>;

/** Which combinations of two dimensions the run executed, every planned one shown. */
export function coverage(input: BlockInput<CoverageProps>): CoverageModel {
  const { props, dimensions, placed, run } = input;
  const rowDim = dimensions.find((d) => d.id === props.rows);
  const colDim = dimensions.find((d) => d.id === props.cols);
  if (!rowDim || !colDim) return { rows: [], cols: [], executed: {}, problem: 'Pick the dimensions for the rows and columns in the report’s layout.' };
  const executed: Record<string, Record<string, string[]>> = {};
  const seenRows: string[] = [];
  const seenCols: string[] = [];
  for (const block of stepsIn(placed, (props.bucket || 'data') as StepBucket)) {
    const at = classify(block.step, [rowDim, colDim], run);
    const row = at[rowDim.id];
    const col = at[colDim.id];
    if (!row || !col) continue;
    seenRows.push(row);
    seenCols.push(col);
    executed[row] = executed[row] ?? {};
    executed[row][col] = [...(executed[row][col] ?? []), block.title];
  }
  return { rows: orderedValues(rowDim, Array.from(new Set(seenRows))), cols: orderedValues(colDim, Array.from(new Set(seenCols))), executed, problem: null };
}

export const coverageBlock: BlockDefinition<CoverageProps> = defineBlock({
  type: 'coverage',
  title: 'Coverage',
  description: 'Every combination of two dimensions of the test matrix, and whether the run executed it.',
  group: 'Results',
  props: coverageProps,
  findings: (input) => {
    const model = coverage(input);
    if (model.problem) return [];
    const planned = model.rows.length * model.cols.length;
    const run = model.rows.reduce((n, r) => n + model.cols.filter((c) => (model.executed[r]?.[c] ?? []).length > 0).length, 0);
    return planned > 0 && run < planned ? [finding.note(`coverage-${input.block.id}`, `${run} of ${planned} planned combinations were executed on this run.`)] : [];
  },
});

export const procedureParametersBlock = defineBlock({
  type: 'procedure-parameters',
  title: 'Test matrix',
  description: 'The procedure’s own parameters: what it loops over (and which this run ran), and the lists it carries, such as limits per region or per function.',
  group: 'Run data',
  props: defineValues({}),
  isEmpty: ({ run }) => isEmptyProcedureParameters(procedureParameters(run)),
});

export const checksBlock = defineBlock({
  type: 'checks',
  title: 'Checks',
  description: 'Every check the run recorded — its validations, and the yes/no results its steps recorded — one row per check with the conditions it was made at, and each yes/no’s values by condition.',
  group: 'Results',
  props: defineValues({
    title: v.text({ label: 'Title', optional: true }),
    details: v.boolean({ label: 'A table of each yes/no result’s values, by condition', default: true }),
  }),
  isEmpty: ({ run }) => runChecks(run).length === 0,
});

// ── the record ──────────────────────────────────────────────────────────────

export const findingsBlock = defineBlock({
  type: 'findings',
  title: 'Findings',
  description: 'Every check the report makes, with its result.',
  group: 'Record',
  props: defineValues({}),
});

export const figureIndexBlock = defineBlock({
  type: 'figure-index',
  title: 'Figure index',
  description: 'Every figure the report prints, numbered in print order, with its source file.',
  group: 'Record',
  props: defineValues({}),
});

export const tableIndexBlock = defineBlock({
  type: 'table-index',
  title: 'Table index',
  description: 'Every numbered table the report prints.',
  group: 'Record',
  props: defineValues({}),
});

export const STANDARD_BLOCKS: ReadonlyArray<BlockDefinition<never>> = [
  textBlock, fieldBlock, readingsBlock, galleryBlock, dutBlock, equipmentBlock, stepsBlock, resultsMatrixBlock, coverageBlock, ...LIMIT_BLOCKS, procedureParametersBlock, checksBlock, dataFilesBlock, findingsBlock, figureIndexBlock, tableIndexBlock,
] as unknown as ReadonlyArray<BlockDefinition<never>>;
